import React, { useEffect, useRef } from "react";
import { FC, ReactNode } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";
import _ from "lodash";
import { Box, Divider, Drawer, Hidden, List, ListSubheader, makeStyles } from "@material-ui/core";
import {
  Disc as DiscIcon,
  Activity as ActivityIcon,
  Mail as MailIcon,
  MessageCircle as MessageCircleIcon,
  Users as UsersIcon,
  CreditCard as CreditCardIcon,
  TrendingUp as TrendingUpIcon,
  DollarSign,
  Columns,
  Tag,
  List as ListIcon,
  Calendar,
  Settings,
  Percent,
  FileText,
  RefreshCw,
  MapPin
} from "react-feather";
import Logo from "src/components/Logo";
import NavItem from "./NavItem";
import { getCurrentUserAccountId, useCurrentUserTeamsAndOrgs } from "../../hooks/commonDataUtils";
import { compute } from "@ollie-sports/core";
import { translate } from "@ollie-sports/i18n";
import { ReceiptOutlined } from "@material-ui/icons";
import {
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  TagIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon
} from "@heroicons/react/24/outline";
import { usePersistentState, useSessionPersistentState } from "../../utils/usePersistentState";
import { CoolMultiSelectInput } from "../../components/Inputs/CoolMultiSelectInput";
import { CoolSelectInput } from "../../components/Inputs/CoolSelectInput";
import { useOrgSettings } from "../../hooks/useOrgSettings";
import { useEligibleOpenOrgEventOrgIds } from "../../hooks/useEligibleOpenOrgEventOrgIds";
import { useSystemStatus } from "../../hooks/useSystemStatus";
import { FeatureToggles } from "@ollie-sports/models";

interface NavBarProps {
  onMobileClose: () => void;
  openMobile: boolean;
}

enum ShowPermission {
  isOrgAdmin = "isOrgAdmin"
}

interface Item {
  href?: string;
  activePath?: string; // Used for highlight matching instead of href when set
  icon?: ReactNode;
  info?: React.ComponentType;
  items?: Item[];
  title: string;
  showItem: boolean;
}

interface Section {
  items: Item[];
  subheader: string;
  showSection: boolean;
}

function renderNavItems({ items, pathname, depth = 0 }: { items: Item[]; pathname: string; depth?: number }) {
  return <List disablePadding>{items.reduce((acc, item) => reduceChildRoutes({ acc, item, pathname, depth }), [])}</List>;
}

function reduceChildRoutes({ acc, pathname, item, depth }: { acc: any[]; pathname: string; item: Item; depth: number }): any {
  const key = item.title + depth;

  const matchPath = item.activePath || item.href;
  let open = matchPath && pathname.toLowerCase().includes(matchPath.toLowerCase());

  // For parent groups without href, auto-expand if any child matches
  if (!open && item.items) {
    open = item.items.some(child => {
      const childMatch = child.activePath || child.href;
      return childMatch && pathname.toLowerCase().includes(childMatch.toLowerCase());
    });
  }
  if (item.items) {
    if (item.showItem) {
      acc.push(
        <NavItem depth={depth} icon={item.icon} info={item.info} key={key} open={!!open} title={item.title}>
          {renderNavItems({
            depth: depth + 1,
            pathname,
            items: item.items
          })}
        </NavItem>
      );
    }
  } else {
    if (item.showItem) {
      acc.push(
        <NavItem depth={depth} href={item.href} activePath={item.activePath} icon={item.icon} info={item.info} key={key} title={item.title} open={!!open} />
      );
    }
  }

  return acc;
}

function BetaBadge() {
  return (
    <span
      style={{
        backgroundColor: "#03E076",
        color: "white",
        fontSize: 10,
        fontWeight: 700,
        padding: "2px 6px",
        borderRadius: 4,
        letterSpacing: 0.5,
        lineHeight: 1
      }}
    >
      BETA
    </span>
  );
}

type OpenOrgEventNavType = "tryout" | "camp" | "genericEvent";

function getOpenOrgEventsNavItem(
  orgId: string,
  types: OpenOrgEventNavType[] = ["tryout", "camp", "genericEvent"]
): Item {
  const allSubItems: { type: OpenOrgEventNavType; title: string }[] = [
    { type: "tryout", title: translate({ defaultMessage: "Tryouts" }) },
    { type: "camp", title: translate({ defaultMessage: "Camps" }) },
    { type: "genericEvent", title: translate({ defaultMessage: "Open Events" }) }
  ];
  return {
    title: translate({ defaultMessage: "Open Org Events" }),
    icon: ClipboardDocumentListIcon,
    showItem: true,
    info: BetaBadge,
    items: allSubItems
      .filter(s => types.includes(s.type))
      .map(s => ({
        title: s.title,
        href: `/app/org/${orgId}/openEvents/${s.type}`,
        activePath: `/app/org/${orgId}/openEvents/${s.type}`,
        icon: ClipboardDocumentListIcon,
        showItem: true
      }))
  };
}

const useStyles = makeStyles(theme => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    zIndex: 30
  },
  avatar: {
    cursor: "pointer",
    width: 64,
    height: 64
  },
  linkContainer: {
    [theme.breakpoints.down("md")]: {
      paddingTop: 0
    }
  }
}));

const NavBar: FC<NavBarProps> = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();

  const { data: teamAndOrgsRaw } = useCurrentUserTeamsAndOrgs();
  // The teams/orgs subscription can momentarily emit `undefined` (re-subscribe, React Query cache GC, a slow
  // Firestore server snapshot, etc.). Without this guard that transient gap collapses the whole Club Tools
  // section and the org selector. Hold the last non-empty value so the nav stays stable across those blips —
  // a genuine "no admin orgs" state still arrives as a DEFINED value (with filtered-empty orgs) and is honored.
  const lastGoodTeamAndOrgsRef = useRef(teamAndOrgsRaw);
  if (teamAndOrgsRaw) {
    lastGoodTeamAndOrgsRef.current = teamAndOrgsRaw;
  }
  const teamAndOrgs = teamAndOrgsRaw ?? lastGoodTeamAndOrgsRef.current;

  const adminOrgs = _.orderBy(
    teamAndOrgs?.orgs.filter(o => compute.org.isOrgAdmin(teamAndOrgs?.orgs ?? [], getCurrentUserAccountId(), o.id)) ?? [],
    a => a.name,
    "asc"
  );

  const isOrgAdmin = !!adminOrgs.length;

  // For clubs the user is NOT an admin of, show Open Org Events only for the event types they're eligible to
  // view (role + restricted-mode aware) AND that actually have recent events. Mirrors the mobile app.
  const { orgIdsWithRecentTryouts, orgIdsWithRecentCamps, orgIdsWithRecentGenericEvents } = useEligibleOpenOrgEventOrgIds();
  const nonAdminOpenOrgEventGroups = _.orderBy(
    (teamAndOrgs?.orgs ?? [])
      .filter(o => !adminOrgs.some(a => a.id === o.id))
      .map(o => {
        const types: OpenOrgEventNavType[] = [];
        if (orgIdsWithRecentTryouts.includes(o.id)) types.push("tryout");
        if (orgIdsWithRecentCamps.includes(o.id)) types.push("camp");
        if (orgIdsWithRecentGenericEvents.includes(o.id)) types.push("genericEvent");
        return { org: o, types };
      })
      .filter(g => g.types.length > 0),
    g => g.org.name,
    "asc"
  );

  const history = useHistory();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const sections: Section[] = [
    {
      subheader: translate({ defaultMessage: "My Tools" }),
      items: [
        {
          title: translate.common.Teams,
          href: "/app/teams",
          icon: ListIcon,
          showItem: true
        },
        {
          title: translate({ defaultMessage: "My Schedule" }),
          href: "/app/events",
          icon: Calendar,
          info: BetaBadge,
          showItem: true
        },
        // {
        //   title: translate({ defaultMessage: "Directory" }),
        //   href: "/app/directory",
        //   icon: UsersIcon,
        //   showItem: isOrgAdmin
        // },
        {
          title: translate({ defaultMessage: "Stats" }),
          icon: ActivityIcon,
          href: "/app/stats/dashboard",
          showItem: isOrgAdmin
        },
        {
          title: translate({ defaultMessage: "Team Trends" }),
          icon: TrendingUpIcon,
          href: "/app/stats/listTeamsWithStats",
          showItem: true
        },
        {
          title: translate({ defaultMessage: "Games" }),
          icon: DiscIcon,
          href: "/app/stats/games",
          showItem: true
        },
        {
          title: translate({ defaultMessage: "My Email" }),
          href: "/app/email",
          icon: MailIcon,
          showItem: true
        },
        {
          title: translate({ defaultMessage: "Chat" }),
          href: "/app/chat",
          icon: MessageCircleIcon,
          info: BetaBadge,
          showItem: true
        }
      ],
      showSection: true
    }
  ];

  const [selectedAdminOrgId, setSelectedAdminOrgId] = usePersistentState({
    key: "selected-admin-org-id",
    initialValue: adminOrgs?.[0]?.id || ""
  });

  //This keeps the navigation side bar in sync when navigating with copied and pasted urls.
  //Only follow the URL when it points at one of the user's ADMIN orgs — otherwise navigating to a
  //coach-only org (e.g. via the team-staff Open Org Events block) would blank out the Club Tools section.
  const urlOrgId = location.pathname.match(/\/app\/org\/(.+?)\//)?.[1];
  useEffect(() => {
    if (urlOrgId && selectedAdminOrgId !== urlOrgId && adminOrgs.some(a => a.id === urlOrgId)) {
      setSelectedAdminOrgId(urlOrgId);
    }
  }, [urlOrgId, selectedAdminOrgId, adminOrgs.map(a => a.id).join(",")]);

  // If the saved selection is no longer one of the user's admin orgs (e.g. they removed themselves from that
  // club), fall back to the first remaining admin org so the Club Tools section + selector don't disappear.
  useEffect(() => {
    if (adminOrgs.length && !adminOrgs.some(a => a.id === selectedAdminOrgId)) {
      setSelectedAdminOrgId(adminOrgs[0].id);
    }
  }, [adminOrgs.map(a => a.id).join(","), selectedAdminOrgId]);

  const selectedAdminOrg = adminOrgs.find(a => a.id === selectedAdminOrgId) ?? adminOrgs[0];

  const { orgSettings } = useOrgSettings({ orgId: selectedAdminOrg?.id ?? "" });
  const systemStatus = useSystemStatus();
  const fieldSchedulerGlobalEnabled = !!systemStatus?.globalFeatureToggles?.[FeatureToggles.fieldScheduler];
  const fieldSchedulerEnabled = fieldSchedulerGlobalEnabled || !!orgSettings?.fieldSchedulerEnabled;

  const hasViewFinancesPermission = selectedAdminOrg
    ? compute.org.hasOrgViewFinanacesPermission({
        accountId: getCurrentUserAccountId(),
        org: selectedAdminOrg
      })
    : false;

  const content = (
    <Box height="100%" display="flex" flexDirection="column" className="pb-12">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} display="flex">
            <RouterLink to="/">
              <Logo variant="dark" />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2} id="navigation-bar" className={classes.linkContainer}>
          {selectedAdminOrg ? (
            <React.Fragment key={selectedAdminOrg.id}>
              <List
                subheader={
                  <ListSubheader disableGutters disableSticky>
                    {adminOrgs.length > 1 ? translate({ defaultMessage: "Club Tools" }) : selectedAdminOrg.name}
                    {adminOrgs.length > 1 ? (
                      <CoolSelectInput
                        value={selectedAdminOrgId}
                        onChange={id => {
                          if (id !== selectedAdminOrgId) {
                            history.push({ pathname: `/app/org/${id}/members` });
                            setSelectedAdminOrgId(id);
                          }
                        }}
                        options={adminOrgs.map(a => ({ label: a.name, value: a.id }))}
                      />
                    ) : null}
                  </ListSubheader>
                }
              >
                {renderNavItems({
                  items: [
                    {
                      title: translate({ defaultMessage: "People" }),
                      icon: UsersIcon,
                      showItem: true,
                      items: [
                        {
                          title: translate.common.Members,
                          href: `/app/org/${selectedAdminOrg.id}/members`,
                          icon: UsersIcon,
                          showItem: true
                        },
                        {
                          title: translate.common.Teams,
                          href: `/app/org/${selectedAdminOrg.id}/teams`,
                          icon: ListIcon,
                          showItem: true
                        },
                        {
                          title: translate.common.TeamTags,
                          href: `/app/org/${selectedAdminOrg.id}/teamTags`,
                          icon: Tag,
                          showItem: true
                        }
                      ]
                    },
                    {
                      title: translate({ defaultMessage: "Scheduling" }),
                      icon: Calendar,
                      showItem: true,
                      info: fieldSchedulerEnabled ? BetaBadge : undefined,
                      items: [
                        {
                          title: translate({ defaultMessage: "Club Schedule" }),
                          href: `/app/org/${selectedAdminOrg.id}/events`,
                          icon: Calendar,
                          showItem: true
                        },
                        {
                          title: translate({ defaultMessage: "Training Schedules" }),
                          href: sessionStorage.getItem("trainingScheduleLastUrl")?.startsWith(`/app/org/${selectedAdminOrg.id}/trainingSchedules`)
                            ? sessionStorage.getItem("trainingScheduleLastUrl")!
                            : `/app/org/${selectedAdminOrg.id}/trainingSchedules`,
                          activePath: `/app/org/${selectedAdminOrg.id}/trainingSchedules`,
                          icon: Calendar,
                          showItem: fieldSchedulerEnabled
                        },
                        {
                          title: translate({ defaultMessage: "Field Schedule" }),
                          href: `/app/org/${selectedAdminOrg.id}/fieldSchedule`,
                          icon: Calendar,
                          showItem: fieldSchedulerEnabled
                        },
                        {
                          title: translate({ defaultMessage: "Venues/Fields" }),
                          href: `/app/org/${selectedAdminOrg.id}/venues`,
                          icon: MapPin,
                          showItem: fieldSchedulerEnabled
                        }
                      ]
                    },
                    getOpenOrgEventsNavItem(selectedAdminOrg.id),
                    {
                      title: translate({ defaultMessage: "Registration" }),
                      icon: ClipboardDocumentCheckIcon,
                      showItem: hasViewFinancesPermission,
                      items: [
                        {
                          title: translate.common.Registrations,
                          href: `/app/org/${selectedAdminOrg.id}/registrations`,
                          icon: ClipboardDocumentCheckIcon,
                          showItem: hasViewFinancesPermission
                        },
                        {
                          title: translate.common.RegistrationPackages,
                          href: `/app/org/${selectedAdminOrg.id}/registrationPackages`,
                          icon: ClipboardDocumentListIcon,
                          showItem: hasViewFinancesPermission
                        },
                        {
                          title: translate({ defaultMessage: "Registration Questions" }),
                          href: `/app/org/${selectedAdminOrg.id}/registrationQuestions`,
                          icon: QuestionMarkCircleIcon,
                          showItem: hasViewFinancesPermission
                        },
                        {
                          title: translate.common.Seasons,
                          href: `/app/org/${selectedAdminOrg.id}/seasons`,
                          icon: Calendar,
                          showItem: hasViewFinancesPermission
                        }
                      ]
                    },
                    {
                      title: translate({ defaultMessage: "Finances" }),
                      icon: DollarSign,
                      showItem: hasViewFinancesPermission,
                      items: [
                        {
                          title: translate.common.Invoices,
                          href: `/app/org/${selectedAdminOrg.id}/invoices`,
                          icon: ReceiptOutlined,
                          showItem: hasViewFinancesPermission
                        },
                        {
                          title: translate.common.Payments,
                          href: `/app/org/${selectedAdminOrg.id}/payments`,
                          icon: DollarSign,
                          showItem: hasViewFinancesPermission
                        },
                        {
                          title: translate.common.Refunds,
                          href: `/app/org/${selectedAdminOrg.id}/refunds`,
                          icon: RefreshCw,
                          showItem: hasViewFinancesPermission
                        },
                        {
                          title: translate.common.PaymentPlans,
                          href: `/app/org/${selectedAdminOrg.id}/paymentPlans`,
                          icon: CreditCardIcon,
                          showItem: hasViewFinancesPermission
                        },
                        {
                          title: translate.common.Coupons,
                          href: `/app/org/${selectedAdminOrg.id}/coupons`,
                          icon: Percent,
                          showItem: hasViewFinancesPermission
                        }
                      ]
                    },
                    {
                      title: translate({ defaultMessage: "Admin" }),
                      icon: Settings,
                      showItem: true,
                      items: [
                        {
                          title: translate.common.Reports,
                          href: `/app/org/${selectedAdminOrg.id}/reports`,
                          icon: FileText,
                          showItem: true
                        },
                        {
                          title: translate.common.Settings,
                          href: `/app/org/${selectedAdminOrg.id}/settings`,
                          icon: Settings,
                          showItem: true
                        }
                      ]
                    }
                  ],
                  pathname: location.pathname
                })}
              </List>
            </React.Fragment>
          ) : null}
          {nonAdminOpenOrgEventGroups.map(group => (
            <List
              key={group.org.id}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {group.org.name}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: [getOpenOrgEventsNavItem(group.org.id, group.types)],
                pathname: location.pathname
              })}
            </List>
          ))}
          {sections
            .filter(i => i.showSection)
            .map(section => (
              <List
                key={section.subheader}
                subheader={
                  <ListSubheader disableGutters disableSticky>
                    {section.subheader}
                  </ListSubheader>
                }
              >
                {renderNavItems({
                  items: section.items.filter(item => item.showItem),
                  pathname: location.pathname
                })}
              </List>
            ))}
        </Box>
        <Divider />
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer anchor="left" classes={{ paper: classes.desktopDrawer }} open variant="persistent">
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

export default NavBar;
